import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
  Dialog,
  DialogContent,
  Button
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';

import { Elements } from '@stripe/react-stripe-js';
import { stripePromise } from '../../../../../../utils/stripe';
import RaffleHero from './RaffleHero/RaffleHero';
import RaffleCarousel from './RaffleCarousel/RaffleCarousel';
import RaffleWinnersCarousel, {
  WinnerItem
} from './RaffleWinnersCarousel/RaffleWinnersCarousel';
import RaffleNFTShowcase from './RaffleNFTShowcase/RaffleNFTShowcase';
import RaffleFAQ, { FAQItem } from './RaffleFAQ/RaffleFAQ';
import RaffleFooter from './RaffleFooter/RaffleFooter';
import SiteHeader from '../../Landing/SiteHeader';
import { useNetwork } from '../../../../../../utils/network';
import RaffleEntryForm from './RaffleForms/RaffleEntryForm';
import { useNavigate } from 'react-router-dom';
import { getYouTubeEmbedUrl } from '../../../../../../utils/youtubeEmbed';
import { imageAssetUrl } from '../../../../../../utils/publicAssets';
import SubscriptionUpgradeCards from './SubscriptionUpgradeCards/SubscriptionUpgradeCards';
import SubscriptionStackingExplainer from './SubscriptionStackingExplainer/SubscriptionStackingExplainer';
import RaffleStripeCheckoutForm from './RafflePaymentPage/RaffleStripeCheckoutForm';
import type { GiveawayConfirmationState } from './RafflePaymentPage/RafflePaymentConfirmationPage';
import type { RafflePaymentPayload } from './RafflePaymentPage/RafflePaymentPage';
import {
  getFirstMonthDiscountedPrice,
  isDiscountEligibleSubscriptionPackage
} from '../../../../../../utils/subscriptionDiscounts';

const giveawayImage = imageAssetUrl('giveaway_image_new.jpg');
const winSticker = imageAssetUrl('WIN_10K_STICKER_NEW_INC.png');
// Stripe.js instance is the shared toggle-aware one imported above (utils/stripe).

export type PackItem = {
  id: string;
  title: string;
  price: string;
  entries: number;
  nfts: number;
  tokens?: number;
  description: string;
  dotColor: string;
  bonus?: {
    description: string;
    extraCards: number;
    extraEntries: number;
    price: number;
  };
};

type ActiveGiveaway = {
  giveawayName: string;
  endDate: string;
  prizeAmount?: number | string | null;
};

type GiveawayStats = {
  completedGiveaways: number;
  totalGivenAwayAud: number;
  activeJackpotParticipantCount?: number;
};

type ApiPackage = {
  id: number;
  name: string;
  type: 'Standalone' | 'Subscription';
  description: string | null;
  imageUrl?: string | null;
  price: string | number;
  cards?: number | null;
  tokens?: number | null;
  entries?: number | null;
  Bonus?: {
    description?: string | null;
    extraCards?: number | null;
    extraEntries?: number | null;
    price?: string | number | null;
  } | null;
};

type ApiWinner = {
  id?: string | number;
  username?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  discordUsername?: string | null;
  discordName?: string | null;
  giveawayName?: string | null;
  prizeAmount?: number | string | null;
  dateWon?: string | Date | null;
  endDate?: string | Date | null;
};

const JACKPOT_PRIZE_AMOUNT_AUD = 10000;
const GIVEAWAY_CONTENT_MAX_WIDTH = 1920;
const GIVEAWAY_CONTENT_MAX_HEIGHT = 700;
const ABN_LOOKUP_URL = 'https://abr.business.gov.au/ABN/View?id=20630834762';
const WINNER_VIDEO_URLS = [
  'https://www.youtube.com/watch?v=aXb81qXMQOw',
  'https://youtu.be/QoZ8YaLrAkE'
];

const safeDate = (d: any): Date | null => {
  if (!d) return null;
  const x = d instanceof Date ? d : new Date(d);
  return Number.isNaN(x.getTime()) ? null : x;
};

const formatPrizeLabel = (value?: number | string | null) => {
  const amount = Number(value);
  const safeAmount =
    Number.isFinite(amount) && amount > 0 ? amount : JACKPOT_PRIZE_AMOUNT_AUD;

  return `A$${safeAmount.toLocaleString('en-AU', {
    maximumFractionDigits: 0
  })} in INC`;
};

const formatShortPrizeLabel = (value?: number | string | null) => {
  const amount = Number(value);
  const safeAmount =
    Number.isFinite(amount) && amount > 0 ? amount : JACKPOT_PRIZE_AMOUNT_AUD;

  return `A$${safeAmount.toLocaleString('en-AU', {
    maximumFractionDigits: 0
  })}`;
};

const formatWinnerName = (w: ApiWinner) => {
  const fn = String(w.firstName || '').trim();
  const ln = String(w.lastName || '').trim();
  const username = String(w.username || '').trim();

  if (fn) {
    const initial =
      ln.length > 0 ? ln.replace('.', '').slice(0, 1).toUpperCase() : '';
    return initial ? `${fn} ${initial}.` : fn;
  }

  return username || 'Winner';
};

type RafflePageProps = {
  packageMode?: 'standalone' | 'subscription';
  entryPaymentPath?: string;
  openSubscriptionCheckoutInPage?: boolean;
};

const RafflePage = ({
  packageMode = 'standalone',
  entryPaymentPath = 'special-offer',
  openSubscriptionCheckoutInPage = false
}: RafflePageProps) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { get } = useNetwork();

  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);

  const [standalonePkgs, setStandalonePkgs] = useState<ApiPackage[]>([]);
  const [subscriptionPkgs, setSubscriptionPkgs] = useState<ApiPackage[]>([]);
  const [loadingPkgs, setLoadingPkgs] = useState(false);
  const [pkgsError, setPkgsError] = useState<string | null>(null);

  const [activeGiveaway, setActiveGiveaway] = useState<ActiveGiveaway | null>(
    null
  );
  const [loadingGiveaway, setLoadingGiveaway] = useState(false);
  const [giveawayError, setGiveawayError] = useState<string | null>(null);

  // ✅ winners
  const [winners, setWinners] = useState<WinnerItem[]>([]);
  const [loadingWinners, setLoadingWinners] = useState(false);
  const [winnersError, setWinnersError] = useState<string | null>(null);
  const [winnerVideoIndex, setWinnerVideoIndex] = useState(0);
  const [giveawayStats, setGiveawayStats] = useState<GiveawayStats | null>(
    null
  );
  const [subscriptionCheckoutPayload, setSubscriptionCheckoutPayload] =
    useState<RafflePaymentPayload | null>(null);
  const [subscriptionCheckoutOpen, setSubscriptionCheckoutOpen] =
    useState(false);

  // The app's global dark theme paints <body> near-black (#121212). On this
  // white page that shows as dark bands at the top/bottom on mobile overscroll
  // (rubber-band). Keep the surround white while the giveaway page is mounted.
  useEffect(() => {
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#ffffff';
    return () => {
      document.body.style.backgroundColor = prev;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadPackages = async () => {
      setLoadingPkgs(true);
      setPkgsError(null);

      try {
        const [standalone, subs] = await Promise.all([
          get('/package/standalone', true),
          get('/package/subscription', true)
        ]);

        if (cancelled) return;

        setStandalonePkgs(
          Array.isArray(standalone) ? standalone : [standalone]
        );
        setSubscriptionPkgs(Array.isArray(subs) ? subs : [subs]);
      } catch (e: any) {
        console.error(e);
        if (!cancelled) setPkgsError(e?.message || 'Failed to load packages');
      } finally {
        if (!cancelled) setLoadingPkgs(false);
      }
    };

    loadPackages();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadGiveawayStats = async () => {
      try {
        const data = (await get('/giveaway/stats', true)) as GiveawayStats;
        if (cancelled) return;

        const completedGiveaways = Number(data?.completedGiveaways || 0);
        const totalGivenAwayAud = Number(data?.totalGivenAwayAud) || 0;
        const activeJackpotParticipantCount = Number(
          data?.activeJackpotParticipantCount || 0
        );

        setGiveawayStats({
          completedGiveaways,
          totalGivenAwayAud,
          activeJackpotParticipantCount
        });
      } catch (error) {
        console.error('Failed to fetch giveaway stats:', error);
        if (!cancelled) setGiveawayStats(null);
      }
    };

    loadGiveawayStats();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadGiveaway = async () => {
      setLoadingGiveaway(true);
      setGiveawayError(null);

      try {
        const data = (await get('/giveaway/countdown', true)) as ActiveGiveaway;
        if (cancelled) return;
        setActiveGiveaway(data);
      } catch (e: any) {
        console.error(e);
        if (!cancelled)
          setGiveawayError(e?.message || 'Failed to load giveaway');
      } finally {
        if (!cancelled) setLoadingGiveaway(false);
      }
    };

    loadGiveaway();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadWinners = async () => {
      setLoadingWinners(true);
      setWinnersError(null);

      try {
        let data: any = null;

        try {
          data = await get('/giveaway/last-winners?limit=10', true);
        } catch {
          data = await get('/giveaway/last-winner', true);
        }

        if (cancelled) return;

        const arr: ApiWinner[] = Array.isArray(data)
          ? data
          : data
            ? [data]
            : [];

        const mapped: WinnerItem[] = arr
          .map((w, idx) => {
            const date = safeDate(w.dateWon ?? w.endDate);
            if (!date) return null;

            const discord =
              (w.discordUsername || w.discordName || '')?.toString().trim() ||
              undefined;

            return {
              id: String(w.id ?? `${date.getTime()}-${idx}`),
              name: formatWinnerName(w),
              winningDate: date,
              prizeAmount: formatPrizeLabel(w.prizeAmount),
              discordUsername: discord
            } as WinnerItem;
          })
          .filter(Boolean) as WinnerItem[];

        setWinners(mapped);
      } catch (e: any) {
        console.error(e);
        if (!cancelled) setWinnersError(e?.message || 'Failed to load winners');
      } finally {
        if (!cancelled) setLoadingWinners(false);
      }
    };

    loadWinners();
    return () => {
      cancelled = true;
    };
  }, []);

  const standaloneDotColours = [
    'linear-gradient(180deg,#B14CFF,#7B1BFF)', // Purple
    'linear-gradient(180deg,#7CFF3A,#3AAE12)', // Green
    'linear-gradient(180deg,#FF4A4A,#B30000)', // Red
    'linear-gradient(180deg,#FFD700,#C79A00)' // Gold
  ];

  const toPackStandaloneItems = (pkgs: ApiPackage[]): PackItem[] =>
    pkgs.map((p, idx) => ({
      id: String(p.id),
      title: p.name,
      // ✅ store as numeric string (no $) so Number(pack.price) works everywhere
      price:
        typeof p.price === 'number'
          ? p.price.toFixed(2)
          : String(p.price).replace(/^\$/, ''),

      entries: Number(p.entries || 0),
      nfts: Number(p.cards || 0),
      description: p.description || '',
      dotColor: standaloneDotColours[idx % standaloneDotColours.length],

      // ✅ attach bonus if it exists
      bonus: p.Bonus
        ? {
            description: p.Bonus.description || '',
            extraCards: Number(p.Bonus.extraCards || 0),
            extraEntries: Number(p.Bonus.extraEntries || 0),
            price: Number(p.Bonus.price || 0) // bonus.price is a number in PackItem type
          }
        : undefined
    }));

  const toPackSubscriptionItems = (pkgs: ApiPackage[]): PackItem[] =>
    pkgs.map((p, idx) => ({
      id: String(p.id),
      title: p.name,
      price: typeof p.price === 'number' ? `${p.price}` : String(p.price),
      entries: Number(p.entries || 0),
      nfts: Number(p.cards || 0),
      tokens: Number(p.tokens || 0),
      description: p.description || '',
      dotColor: standaloneDotColours[idx % standaloneDotColours.length]
    }));

  const standaloneItems = useMemo(
    () => toPackStandaloneItems(standalonePkgs),
    [standalonePkgs]
  );
  const subscriptionItems = useMemo(
    () => toPackSubscriptionItems(subscriptionPkgs),
    [subscriptionPkgs]
  );
  const displayedSubscriptionItems = useMemo(
    () =>
      packageMode === 'subscription'
        ? [...subscriptionItems].sort(
            (a, b) => Number(a.price || 0) - Number(b.price || 0)
          )
        : subscriptionItems,
    [packageMode, subscriptionItems]
  );
  const drawDate = useMemo(() => {
    if (!activeGiveaway?.endDate) return null;
    const d = new Date(activeGiveaway.endDate);
    return Number.isNaN(d.getTime()) ? null : d;
  }, [activeGiveaway?.endDate]);

  const activePrizeLabel = useMemo(
    () => formatShortPrizeLabel(activeGiveaway?.prizeAmount),
    [activeGiveaway?.prizeAmount]
  );

  const selectedPack = useMemo(() => {
    const sourceItems =
      packageMode === 'subscription' ? subscriptionItems : standaloneItems;
    return sourceItems.find((p) => p.id === selectedId);
  }, [packageMode, standaloneItems, subscriptionItems, selectedId]);

  const packageName = selectedPack?.title ?? '';
  const completedGiveawaysLabel = (
    giveawayStats?.completedGiveaways ?? 0
  ).toLocaleString('en-AU');
  const totalGivenAwayLabel = `A$${(
    giveawayStats?.totalGivenAwayAud ?? 0
  ).toLocaleString('en-AU', {
    maximumFractionDigits: 0
  })}`;
  const activeJackpotParticipantCountLabel = (
    giveawayStats?.activeJackpotParticipantCount ?? 0
  ).toLocaleString('en-AU');
  const closeEntryModal = () => {
    setSelectedId(undefined);
  };

  const isDiscountEligibleSubscriptionPack = (pack: PackItem) =>
    isDiscountEligibleSubscriptionPackage(pack.id);

  const getDisplayedSubscriptionPrice = (pack: PackItem) => {
    const price = Number(pack.price || 0);
    return isDiscountEligibleSubscriptionPack(pack)
      ? getFirstMonthDiscountedPrice(price)
      : price;
  };

  const getSignupCodeBonusEntries = (pack: PackItem) =>
    Math.max(0, Math.floor(Number(pack.entries || 0) * 10));

  const openSubscriptionCheckout = async (payload: RafflePaymentPayload) => {
    const signupCodeBonusEntries = getSignupCodeBonusEntries(
      payload.selectedPack
    );
    const checkoutPayload: RafflePaymentPayload = {
      ...payload,
      signupCodeBonusEntries,
      signupCodeBonusSource: 'giveaway_subs',
      signupSource: 'giveaway-subs'
    };

    sessionStorage.setItem(
      'rafflePaymentPayload',
      JSON.stringify(checkoutPayload)
    );
    setSubscriptionCheckoutPayload(checkoutPayload);
    closeEntryModal();
    setSubscriptionCheckoutOpen(true);
  };

  const closeSubscriptionCheckout = () => {
    setSubscriptionCheckoutOpen(false);
    setSubscriptionCheckoutPayload(null);
  };

  const saveSubscriptionConfirmationDraft = (
    payload: RafflePaymentPayload,
    paymentIntentId?: string,
    accountCreated?: boolean
  ) => {
    const pack = payload.selectedPack;
    const signupCodeBonusEntries = getSignupCodeBonusEntries(pack);
    const totalEntries =
      Number(pack.entries || 0) + Number(signupCodeBonusEntries || 0);
    const confirmationState: GiveawayConfirmationState = {
      entry: {
        firstName: payload.entry.firstName ?? '',
        lastName: payload.entry.lastName ?? '',
        username: payload.entry.username ?? '',
        email: payload.entry.email ?? ''
      },
      selectedPack: pack,
      totalEntries,
      nftRewards: Number(pack.nfts || 0),
      amountPaidAud: getDisplayedSubscriptionPrice(pack),
      checkoutMode: 'subscription',
      incPerMonth: Number(pack.tokens || 0),
      /* Whether to greet this buyer as a brand-new member.
       *
       * The raffle unlock token this used to read is no longer minted by the entry
       * form, so it was always false and every returning member was told their
       * login details had been emailed to them — an email that is only ever sent
       * for an account that was actually created. The purchase reports what it
       * really did; fall back to the old check if a path ever stops saying. */
      isExistingVerifiedUser:
        accountCreated !== undefined
          ? !accountCreated
          : Boolean(payload.raffleUnlockToken),
      memberPortalPath: '/login',
      bonusSelected: false,
      paymentIntentId
    };

    sessionStorage.setItem(
      'giveawayConfirmationState',
      JSON.stringify(confirmationState)
    );
  };

  const scrollToPackages = () => {
    document.getElementById('raffle-packages')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  const faqItems: FAQItem[] = [
    {
      question: 'Is this legal in Australia?',
      answer: [
        'Yes, we are an Australian registered company.',
        'ABN: 20630834762.',
        'Permit Number is TP/03891.'
      ]
    },
    {
      question: 'How does the giveaway work?',
      answer: [
        '1. Purchase your pack to automatically be in the draw.',
        '2. The winner is drawn live every Friday at 10pm in our Discord.',
        '3. The winner will be contacted via mobile and/or Discord.',
        '4. Funds will be transferred within 48 hours of the draw.'
      ]
    },
    {
      question: 'When is the draw date?',
      answer: ['The winner is drawn every Friday at 10PM AEDT.']
    },
    {
      question: 'How will I receive my INC Token if I win?',
      answer: [
        'The winner will be contacted via email, mobile or Discord within 24 hours of the draw. INC Token will be sent directly to your provided wallet address.'
      ]
    },
    {
      question: 'What are Digital Collectibles and the Digital Collectible rewards I get from a purchase?',
      answer: [
        'Digital Collectibles are collectible items. Our Digital Collectibles can be used in our trading card game and/or our RPG game, which can be found on the Epic Games Store. They can also be traded within our marketplace in the member portal. Each pack includes exclusive Digital Collectible rewards that are claimable in your member portal instantly after purchase.'
      ]
    }
  ];

  const GiveawayTotalsSection = giveawayStats ? (
    <Box
      sx={{
        width: '100%',
        maxWidth: 980,
        mx: 'auto',
        mt: { xs: 3, md: 4 },
        mb: 0,
        px: { xs: 2.5, sm: 0 }
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: { xs: 1.5, sm: 2 },
          p: { xs: 2, sm: 2.5 },
          borderRadius: 4,
          background:
            'linear-gradient(135deg, #07111f 0%, #0f172a 52%, #082f49 100%)',
          border: '1px solid rgba(14,165,233,0.35)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12)'
        }}
      >
        <Box
          sx={{
            textAlign: 'center',
            p: { xs: 2, sm: 2.5 },
            borderRadius: 3,
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.12)'
          }}
        >
          <Typography
            sx={{
              color: '#34d399',
              fontSize: { xs: 36, md: 46 },
              fontWeight: 950,
              lineHeight: 1
            }}
          >
            {activeJackpotParticipantCountLabel}
          </Typography>
          <Typography
            sx={{
              mt: 1,
              color: '#ffffff',
              fontSize: { xs: 13, md: 15 },
              fontWeight: 900,
              letterSpacing: '0.08em',
              textTransform: 'uppercase'
            }}
          >
            Users In Upcoming $10k Giveaway
          </Typography>
        </Box>

        <Box
          sx={{
            textAlign: 'center',
            p: { xs: 2, sm: 2.5 },
            borderRadius: 3,
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.12)'
          }}
        >
          <Typography
            sx={{
              color: '#7dd3fc',
              fontSize: { xs: 36, md: 46 },
              fontWeight: 950,
              lineHeight: 1
            }}
          >
            {completedGiveawaysLabel}
          </Typography>
          <Typography
            sx={{
              mt: 1,
              color: '#ffffff',
              fontSize: { xs: 13, md: 15 },
              fontWeight: 900,
              letterSpacing: '0.08em',
              textTransform: 'uppercase'
            }}
          >
            Completed Giveaways
          </Typography>
        </Box>

        <Box
          sx={{
            textAlign: 'center',
            p: { xs: 2, sm: 2.5 },
            borderRadius: 3,
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.12)'
          }}
        >
          <Typography
            sx={{
              color: '#facc15',
              fontSize: { xs: 34, md: 44 },
              fontWeight: 950,
              lineHeight: 1
            }}
          >
            {totalGivenAwayLabel}
          </Typography>
          <Typography
            sx={{
              mt: 1,
              color: '#ffffff',
              fontSize: { xs: 13, md: 15 },
              fontWeight: 900,
              letterSpacing: '0.08em',
              textTransform: 'uppercase'
            }}
          >
            Given Away
          </Typography>
        </Box>
      </Box>
    </Box>
  ) : null;

  const PreviousWinnersCarouselSection = loadingWinners ? (
    <Typography
      sx={{
        textAlign: 'center',
        color: 'black',
        fontWeight: 800
      }}
    >
      Loading winners…
    </Typography>
  ) : winnersError ? (
    <Typography
      sx={{
        textAlign: 'center',
        color: 'rgba(255,120,120,0.85)',
        fontWeight: 800
      }}
    >
      {winnersError}
    </Typography>
  ) : winners.length === 0 ? (
    <Typography
      sx={{
        textAlign: 'center',
        color: 'black',
        fontWeight: 800
      }}
    >
      No winners yet.
    </Typography>
  ) : (
    <RaffleWinnersCarousel winners={winners} />
  );

  const WinnersSection = (
    <Box>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: 980,
          mx: 'auto',
          mb: 4
        }}
      >
        <Box
          sx={{
            display: 'flex',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              transform: `translateX(-${winnerVideoIndex * 100}%)`,
              transition: 'transform 280ms ease'
            }}
          >
            {WINNER_VIDEO_URLS.map((url, index) => {
              const embedUrl = getYouTubeEmbedUrl(url);
              if (!embedUrl) return null;

              return (
                <Box
                  key={url}
                  sx={{
                    flex: '0 0 100%',
                    px: { xs: 0.5, sm: 0 }
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      borderRadius: 4,
                      overflow: 'hidden',
                      border: '1px solid rgba(124,255,250,0.16)',
                      background: 'rgba(12, 18, 20, 0.55)',
                      boxShadow: '0 18px 70px rgba(0,0,0,0.18)'
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        width: '100%',
                        paddingTop: '56.25%'
                      }}
                    >
                      <Box
                        component="iframe"
                        title={`Incrypt winner video ${index + 1}`}
                        src={embedUrl}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          width: '100%',
                          height: '100%',
                          border: 0
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {WINNER_VIDEO_URLS.length > 1 && (
          <>
            <IconButton
              onClick={() =>
                setWinnerVideoIndex((index) =>
                  index === 0 ? WINNER_VIDEO_URLS.length - 1 : index - 1
                )
              }
              sx={{
                display: 'inline-flex',
                position: 'absolute',
                top: '50%',
                left: { xs: -4, sm: -44, md: -58 },
                transform: 'translateY(-50%)',
                zIndex: 2,
                width: { xs: 38, sm: 46 },
                height: { xs: 38, sm: 46 },
                background: 'rgba(15,23,42,0.72)',
                color: '#ffffff',
                border: '1px solid rgba(255,255,255,0.28)',
                backdropFilter: 'blur(8px)',
                '&:hover': { background: 'rgba(15,23,42,0.9)' }
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              onClick={() =>
                setWinnerVideoIndex((index) =>
                  index === WINNER_VIDEO_URLS.length - 1 ? 0 : index + 1
                )
              }
              sx={{
                display: 'inline-flex',
                position: 'absolute',
                top: '50%',
                right: { xs: -4, sm: -44, md: -58 },
                transform: 'translateY(-50%)',
                zIndex: 2,
                width: { xs: 38, sm: 46 },
                height: { xs: 38, sm: 46 },
                background: 'rgba(15,23,42,0.72)',
                color: '#ffffff',
                border: '1px solid rgba(255,255,255,0.28)',
                backdropFilter: 'blur(8px)',
                '&:hover': { background: 'rgba(15,23,42,0.9)' }
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </>
        )}

        {WINNER_VIDEO_URLS.length > 1 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 1,
              mt: 2
            }}
          >
            {WINNER_VIDEO_URLS.map((url, index) => (
              <Box
                key={url}
                onClick={() => setWinnerVideoIndex(index)}
                sx={{
                  width: winnerVideoIndex === index ? 34 : 10,
                  height: 10,
                  borderRadius: 999,
                  cursor: 'pointer',
                  transition: 'width 180ms ease, background 180ms ease',
                  background:
                    winnerVideoIndex === index
                      ? '#0f172a'
                      : 'rgba(15,23,42,0.24)'
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      {packageMode === 'subscription' ? (
        <Box sx={{ mb: { xs: 3, md: 4 } }}>
          {PreviousWinnersCarouselSection}
        </Box>
      ) : (
        PreviousWinnersCarouselSection
      )}

      {packageMode === 'subscription' && renderPackagesSection()}
    </Box>
  );

  function renderPackagesSection() {
    return (
      <Box
        sx={{
          width: {
            xs: '100%',
            lg: packageMode === 'subscription' ? 'min(96vw, 1840px)' : '100%'
          },
          maxWidth:
            packageMode === 'subscription' ? { xs: '100%', lg: 1840 } : '100%',
          ml: {
            xs: 0,
            lg: packageMode === 'subscription' ? '50%' : 0
          },
          transform: {
            xs: 'none',
            lg: packageMode === 'subscription' ? 'translateX(-50%)' : 'none'
          },
          px: { xs: 1.5, sm: 2.5, md: 4 },
          py: packageMode === 'subscription' ? { xs: 2.25, md: 3.5 } : 0,
          background: 'transparent',
          borderTop: 'none',
          borderBottom: 'none',
          overflowX: 'hidden'
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth:
              packageMode === 'subscription'
                ? { xs: '100%', lg: 1840 }
                : GIVEAWAY_CONTENT_MAX_WIDTH,
            mx: 'auto'
          }}
        >
          <Box
            id="raffle-packages"
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr)',
              alignItems: 'start',
              width: '100%',
              maxWidth: '100%',
              scrollMarginTop: {
                xs: '150px',
                sm: '165px',
                md: '175px',
                lg: '24px'
              }
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              {packageMode === 'subscription' ? (
                <Box>
                  <Box
                    sx={{
                      maxWidth: { xs: 1120, xl: 1440 },
                      mx: 'auto',
                      mb: { xs: 3.5, md: 4.5 },
                      textAlign: 'center',
                      px: { xs: 1, sm: 2 }
                    }}
                  >
                    <Typography
                      component="h2"
                      sx={{
                        color: '#0f172a',
                        fontSize: { xs: 34, sm: 44, md: 58 },
                        fontWeight: 950,
                        lineHeight: 0.98,
                        letterSpacing: '0'
                      }}
                    >
                      Select Your Subscription Tier
                    </Typography>
                    <Typography
                      sx={{
                        mt: 1.5,
                        color: '#475569',
                        fontSize: { xs: 16, md: 20 },
                        fontWeight: 800,
                        lineHeight: 1.35
                      }}
                    >
                      Choose the monthly entry stack that fits how much you want
                      to boost every draw.
                    </Typography>
                  </Box>

                  <SubscriptionStackingExplainer
                    items={displayedSubscriptionItems}
                  />

                  <Box
                    sx={{
                      px: { xs: 0.25, md: 0 },
                      py: 0
                    }}
                  >
                    <SubscriptionUpgradeCards
                      items={displayedSubscriptionItems}
                      selectedId={selectedId}
                      onSelect={(id) => setSelectedId(id)}
                    />
                  </Box>
                </Box>
              ) : (
                <RaffleCarousel
                  items={standaloneItems}
                  selectedId={selectedId}
                  onSelect={(id) => setSelectedId(id)}
                  prizeLabel={activePrizeLabel}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: 'white' }}>
      <SiteHeader />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          overflow: 'visible'
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: GIVEAWAY_CONTENT_MAX_WIDTH,
            maxHeight: GIVEAWAY_CONTENT_MAX_HEIGHT,
            height: {
              xs: 'clamp(265px, 46vh, 380px)',
              sm: 'clamp(360px, 56vh, 485px)',
              md: 'clamp(500px, 68vh, 640px)',
              lg: GIVEAWAY_CONTENT_MAX_HEIGHT
            },
            overflow: 'hidden'
          }}
        >
          <Box
            component="img"
            src={giveawayImage}
            alt="heroImage"
            sx={{
              width: { xs: '110%', sm: '106%', md: '100%' },
              height: { xs: '110%', sm: '106%', md: '100%' },
              objectFit: 'cover',
              objectPosition: 'center center',
              transform: {
                xs: 'translate(-5%, -8%)',
                sm: 'translate(-3%, -5%)',
                md: 'none'
              },
              display: 'block'
            }}
          />
          <Box
            component="img"
            src={winSticker}
            alt="stickerText"
            sx={{
              width: {
                xs: '96%',
                sm: '78%',
                md: '60%',
                lg: '52%',
                xl: '46%'
              },
              maxWidth: 1000,
              height: 'auto',
              position: 'absolute',
              top: { xs: '65%', sm: '60%', md: '3%', lg: '-4%' },
              left: '50%',
              transform: {
                xs: 'translate(-50%, -50%)',
                sm: 'translate(-50%, -50%)',
                md: 'translateX(-50%)'
              },
              zIndex: 1
            }}
          />

          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: { xs: 14, sm: 18, md: 24, lg: 166 },
              zIndex: 2,
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: { xs: 0.8, sm: 1.2 },
              px: 2
            }}
          >
            {[
              {
                label: 'Permit Number: TP/03891',
                href: undefined
              },
              {
                label: 'ABN: 20630834762',
                href: ABN_LOOKUP_URL
              }
            ].map((meta) => (
              <Box
                key={meta.label}
                {...(meta.href
                  ? {
                      component: 'a',
                      href: meta.href,
                      target: '_blank',
                      rel: 'noopener noreferrer'
                    }
                  : {})}
                sx={{
                  px: { xs: 1.2, sm: 1.7 },
                  py: { xs: 0.55, sm: 0.75 },
                  borderRadius: 999,
                  border: '1px solid rgba(255,255,255,0.32)',
                  background: 'rgba(255,255,255,0.82)',
                  backdropFilter: 'blur(8px)',
                  color: '#0f172a',
                  fontSize: { xs: 11, sm: 13, md: 14 },
                  fontWeight: 900,
                  letterSpacing: '0.01em',
                  boxShadow: '0 10px 28px rgba(15,23,42,0.16)',
                  textDecoration: 'none',
                  cursor: meta.href ? 'pointer' : 'default',
                  transition: 'transform 160ms ease, background 160ms ease',
                  ...(meta.href
                    ? {
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          background: 'rgba(255,255,255,0.96)'
                        }
                      }
                    : {})
                }}
              >
                {meta.label}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          position: { xs: 'fixed', lg: 'relative' },
          left: { xs: 0, lg: 'auto' },
          right: { xs: 0, lg: 'auto' },
          bottom: { xs: 0, lg: 'auto' },
          zIndex: { xs: 1200, lg: 2 },
          p: { xs: 0, lg: 0 },
          background: {
            xs: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.94) 28%, #ffffff 100%)',
            lg: 'transparent'
          },
          mt: { xs: 0, lg: '-116px' },
          mb: { xs: 0, lg: '36px' }
        }}
      >
        <Box
          onClick={() => {
            document.getElementById('raffle-packages')?.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }}
          sx={{
            width: { xs: '100%', sm: 'min(620px, 100%)', lg: '70%' },
            maxWidth: GIVEAWAY_CONTENT_MAX_WIDTH,
            background:
              'linear-gradient(135deg, #07111f 0%, #0f172a 54%, #052e16 100%)',
            minHeight: { xs: 72, sm: 76, lg: 88 },
            display: 'flex',
            justifyContent: { xs: 'space-between', lg: 'center' },
            alignItems: 'center',
            gap: { xs: 1.5, sm: 2, lg: 0 },
            px: { xs: 2, sm: 2.5, lg: 0 },
            py: { xs: 1.25, lg: 0 },
            borderRadius: { xs: 0, lg: 4 },
            cursor: 'pointer',
            zIndex: 2,
            border: {
              xs: '1px solid rgba(34,197,94,0.34)',
              lg: '1px solid #ffffff'
            },
            boxShadow: {
              xs: '0 14px 34px rgba(15,23,42,0.22), inset 0 1px 0 rgba(255,255,255,0.12)',
              lg: '0 22px 70px rgba(15,23,42,0.22), inset 0 1px 0 rgba(255,255,255,0.12)'
            },
            transition: 'transform 180ms ease, box-shadow 180ms ease',
            '&:hover': {
              transform: { xs: 'none', lg: 'translateY(-2px)' },
              boxShadow: {
                xs: '0 14px 34px rgba(15,23,42,0.22), inset 0 1px 0 rgba(255,255,255,0.12)',
                lg: '0 26px 78px rgba(15,23,42,0.26), inset 0 1px 0 rgba(255,255,255,0.14)'
              }
            }
          }}
        >
          <Box sx={{ display: { xs: 'block', lg: 'none' }, minWidth: 0 }}>
            <Typography
              sx={{
                color: '#ffffff',
                fontSize: { xs: 15, sm: 17 },
                fontWeight: 950,
                lineHeight: 1.05,
                whiteSpace: 'nowrap'
              }}
            >
              Ready to enter?
            </Typography>
            <Typography
              sx={{
                mt: 0.35,
                color: 'rgba(255,255,255,0.72)',
                fontSize: { xs: 11, sm: 12 },
                fontWeight: 800,
                lineHeight: 1.15
              }}
            >
              Secure your entries before the timer ends.
              <Box
                component="span"
                sx={{
                  display: 'block',
                  mt: 0.25,
                  color: '#86efac',
                  fontWeight: 950
                }}
              >
                From {packageMode === 'subscription' ? 'A$2.28' : 'A$9.90'}
              </Box>
            </Typography>
          </Box>

          <Box
            sx={{
              display: { xs: 'flex', lg: 'none' },
              flexShrink: 0,
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: { xs: 118, sm: 148 },
              px: { xs: 1.8, sm: 2.6 },
              py: { xs: 1.05, sm: 1.2 },
              borderRadius: 999,
              backgroundColor: '#16a34a',
              color: '#ffffff',
              boxShadow: '0 12px 28px rgba(22,163,74,0.28)',
              transformOrigin: 'center',
              animation: 'mobileCtaJiggle 3.2s ease-in-out infinite',
              '@keyframes mobileCtaJiggle': {
                '0%, 74%, 100%': {
                  transform: 'translateX(0) rotate(0deg)'
                },
                '78%': {
                  transform: 'translateX(-4px) rotate(-3deg)'
                },
                '82%': {
                  transform: 'translateX(4px) rotate(3deg)'
                },
                '86%': {
                  transform: 'translateX(-3px) rotate(-2deg)'
                },
                '90%': {
                  transform: 'translateX(3px) rotate(2deg)'
                },
                '94%': {
                  transform: 'translateX(0) rotate(0deg)'
                }
              }
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: 14, sm: 16 },
                fontWeight: 950,
                letterSpacing: '0.02em',
                lineHeight: 1
              }}
            >
              ENTER NOW
            </Typography>
          </Box>

          <Typography
            sx={{
              display: { xs: 'none', lg: 'block' },
              fontSize: 40,
              fontWeight: 950,
              letterSpacing: '0.03em',
              color: '#ffffff',
              textShadow: '0 2px 18px rgba(0,0,0,0.28)'
            }}
          >
            ENTER NOW - FROM {packageMode === 'subscription' ? 'A$2.28' : 'A$9.90'}
          </Typography>
        </Box>
      </Box>
      {drawDate && (
        <RaffleHero
          drawDate={drawDate}
          prizeLabel={activePrizeLabel}
          prizeUnitLabel="INC Token"
          winnersSection={WinnersSection}
          totalsSection={GiveawayTotalsSection}
          activeJackpotParticipantCount={
            giveawayStats?.activeJackpotParticipantCount
          }
        />
      )}

      <RaffleNFTShowcase />

      {packageMode !== 'subscription' && renderPackagesSection()}

      <Dialog
        open={!!selectedPack}
        onClose={closeEntryModal}
        fullScreen={isSmallScreen}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 4 },
            background: '#ffffff',
            overflow: 'hidden'
          }
        }}
      >
        <DialogContent
          sx={{
            position: 'relative',
            p: { xs: 2.5, sm: 4 },
            maxHeight: { xs: '100vh', sm: 'min(86vh, 900px)' },
            overflowY: 'auto'
          }}
        >
          <IconButton
            aria-label="Close entry form"
            onClick={closeEntryModal}
            sx={{
              position: 'sticky',
              top: 0,
              ml: 'auto',
              mr: { xs: -1, sm: -1.5 },
              mb: { xs: 1, sm: 1.5 },
              display: 'flex',
              zIndex: 2,
              color: '#0f172a',
              background: 'rgba(255,255,255,0.92)',
              border: '1px solid rgba(15,23,42,0.08)',
              boxShadow: '0 8px 20px rgba(15,23,42,0.08)',
              '&:hover': {
                background: '#f8fafc'
              }
            }}
          >
            <CloseIcon />
          </IconButton>

          {selectedPack && (
            <RaffleEntryForm
              packageName={packageName}
              paymentPath={entryPaymentPath}
              onReadyToPay={
                openSubscriptionCheckoutInPage && packageMode === 'subscription'
                  ? openSubscriptionCheckout
                  : undefined
              }
              selectedPack={selectedPack}
              standaloneItems={standaloneItems}
              subscriptionItems={subscriptionItems}
              signupSource={
                packageMode === 'subscription' ? 'giveaway-subs' : 'giveaway'
              }
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={subscriptionCheckoutOpen}
        fullScreen={isSmallScreen}
        maxWidth={false}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') return;
          if (reason === 'escapeKeyDown') return;
          closeSubscriptionCheckout();
        }}
        PaperProps={{
          sx: {
            width: isSmallScreen ? '100%' : '50vw',
            maxWidth: isSmallScreen ? '100%' : 760,
            borderRadius: isSmallScreen ? 0 : 4,
            background: '#ffffff',
            border: '1px solid rgba(18,31,38,0.10)',
            boxShadow: '0 24px 64px rgba(18,31,38,0.14)'
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1.5,
            borderBottom: '1px solid rgba(18,31,38,0.08)'
          }}
        >
          <Typography sx={{ fontWeight: 950, color: '#111827', fontSize: 16 }}>
            Checkout
          </Typography>

          <Box sx={{ ml: 'auto' }}>
            <IconButton
              onClick={closeSubscriptionCheckout}
              sx={{ color: 'rgba(17,24,39,0.68)' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {subscriptionCheckoutPayload && (
            <Elements
              key={`giveaway-subs:inline:${subscriptionCheckoutPayload.selectedPack.id}:${Math.round(
                getDisplayedSubscriptionPrice(
                  subscriptionCheckoutPayload.selectedPack
                ) * 100
              )}`}
              stripe={stripePromise}
              options={{
                mode: 'subscription',
                currency: 'aud',
                paymentMethodTypes: ['card'],
                setupFutureUsage: 'off_session',
                amount: Math.round(
                  getDisplayedSubscriptionPrice(
                    subscriptionCheckoutPayload.selectedPack
                  ) * 100
                ),
                appearance: {
                  theme: 'stripe',
                  variables: {
                    fontSizeBase: '16px'
                  },
                  rules: { '.Label': { color: '#111827' } }
                }
              }}
            >
              <RaffleStripeCheckoutForm
                payload={subscriptionCheckoutPayload}
                mode="subscription"
                pack={subscriptionCheckoutPayload.selectedPack}
                subDiscount={30}
                discountAllSubscriptionPacks={isDiscountEligibleSubscriptionPack(
                  subscriptionCheckoutPayload.selectedPack
                )}
                onPaid={({ paymentIntentId, accountCreated }) => {
                  saveSubscriptionConfirmationDraft(
                    subscriptionCheckoutPayload,
                    paymentIntentId,
                    accountCreated
                  );
                  closeSubscriptionCheckout();
                  navigate('/giveaway-subs/confirmation', { replace: true });
                }}
              />
            </Elements>
          )}
        </Box>
      </Dialog>

      <Box>
        <RaffleFAQ items={faqItems} />
      </Box>

      <Box
        sx={{
          display: { xs: 'none', lg: 'flex' },
          justifyContent: 'center',
          px: 4,
          mt: 7,
          mb: 2
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 980,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 4,
            px: 5,
            py: 4,
            borderRadius: 4,
            background:
              'linear-gradient(135deg, #07111f 0%, #0f172a 52%, #052e16 100%)',
            border: '1px solid rgba(34,197,94,0.34)',
            boxShadow: '0 22px 70px rgba(15,23,42,0.18)'
          }}
        >
          <Box>
            <Typography
              sx={{
                color: '#ffffff',
                fontSize: 30,
                fontWeight: 950,
                lineHeight: 1.1
              }}
            >
              Ready to enter the next draw?
            </Typography>
            <Typography
              sx={{
                mt: 1,
                color: 'rgba(255,255,255,0.72)',
                fontSize: 16,
                fontWeight: 700
              }}
            >
              Choose your pack and secure your entries before the timer ends.
            </Typography>
          </Box>

          <Button
            onClick={scrollToPackages}
            sx={{
              flexShrink: 0,
              px: 4.5,
              py: 1.4,
              borderRadius: 999,
              backgroundColor: '#16a34a',
              color: '#ffffff',
              fontSize: 17,
              fontWeight: 950,
              boxShadow: '0 14px 30px rgba(22,163,74,0.32)',
              '&:hover': {
                backgroundColor: '#15803d'
              }
            }}
          >
            Enter Now
          </Button>
        </Box>
      </Box>

      <RaffleFooter ctaInset />
    </Box>
  );
};

export default RafflePage;
